import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Landmark, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SummaryCards, ActionList, SeparationPanel } from "@/components/realportfolio/SummaryCards";
import { HoldingsTable, PositionsTable, FundsTable } from "@/components/realportfolio/DetailTables";
import { utc } from "@/components/sync/syncText";

const TABS = [["holdings", "Holdings"], ["positions", "Positions (unverified)"], ["funds", "Funds fields"]];

export function Unlock({ onGrant }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.post("/auth/broker/portfolio-step-up", { totp_code: code });
      const { data } = await api.post("/portfolio/real/unlock", { step_up_token: s.step_up_token });
      onGrant(data.grant, data.expires_in_seconds);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCode(""); setBusy(false); }
  };
  return (
    <div data-testid="rp-unlock" className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
      <Lock className="w-3.5 h-3.5" /> Real-account details need a fresh authenticator code (unlocked for 10 min).
      <Input data-testid="rp-unlock-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="h-7 w-28 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
      <Button size="sm" data-testid="rp-unlock-btn" disabled={code.length !== 6 || busy} onClick={go} className="h-7 bg-blue-600 hover:bg-blue-500 text-white">Unlock details</Button>
    </div>
  );
}

function Details({ grant, onLock }) {
  const [tab, setTab] = useState("holdings");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {TABS.map(([k, l]) => <button key={k} data-testid={`rp-tab-${k}`} onClick={() => setTab(k)} className={`px-2.5 py-1 rounded text-[11px] transition-colors ${tab === k ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>)}
        </div>
        <button data-testid="rp-lock" onClick={onLock} className="text-xs text-slate-300 underline underline-offset-2 hover:text-white">Lock details</button>
      </div>
      {tab === "holdings" && <HoldingsTable grant={grant} />}
      {tab === "positions" && <PositionsTable grant={grant} />}
      {tab === "funds" && <FundsTable grant={grant} />}
    </div>
  );
}

export default function RealPortfolio() {
  const [grant, setGrant] = useState(null);
  const [ttl, setTtl] = useState(0);
  const lock = async () => {
    try {
      await api.post("/portfolio/real/lock", {}, { headers: { "X-Portfolio-Grant": grant } });
    } catch (e) {
      toast.error("Lock request failed; details hidden locally and expire within 10 min");
    } finally { setGrant(null); }
  };
  useEffect(() => {
    if (!grant) { return undefined; }
    const t = setTimeout(() => setGrant(null), ttl * 1000);
    return () => clearTimeout(t);
  }, [grant, ttl]);
  const { data: s, isLoading, isError, refetch } = useQuery({
    queryKey: ["rp-summary"], gcTime: 0,
    queryFn: async () => (await api.get("/portfolio/real/summary")).data,
  });
  return (
    <div data-testid="real-portfolio-page">
      <PageHeader title="Real Portfolio · Sharekhan" subtitle="Read-only view of your real broker snapshots — separate from demo and paper trading" icon={Landmark} demo={false} testId="real-portfolio-header" />
      {isLoading && <LoadingState label="Loading real-account status…" />}
      {isError && <ErrorState label="Real-account status could not be loaded" onRetry={refetch} />}
      {s && (
        <div className="space-y-4">
          <Panel title="Synchronization status" testId="rp-status-panel" actions={<ShieldCheck className="w-4 h-4 text-slate-500" />}>
            <div className="p-4 space-y-3 text-xs">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
                <span data-testid="rp-last-run">Last sync: {s.last_run ? `${s.last_run.status} · ${utc(s.last_run.finished_at)}` : "none"}</span>
                <span>Last successful: {utc(s.last_successful_run?.finished_at)}</span>
                <span data-testid="rp-live">Live mode: {s.live_enabled ? "ON" : "OFF"}</span>
                <span data-testid="rp-orders">Orders possible: no</span>
                <span data-testid="rp-funds-completeness">Funds: {s.funds.verified_fields}/{s.funds.observed_fields} fields verified · totals not calculated</span>
              </div>
              <SummaryCards s={s} />
              <ActionList actions={s.actions} />
            </div>
          </Panel>
          <SeparationPanel s={s} />
          <Panel title="Real account details" testId="rp-details-panel">
            <div className="p-4">{grant ? <Details grant={grant} onLock={lock} /> : <Unlock onGrant={(g, s) => { setTtl(s); setGrant(g); }} />}</div>
          </Panel>
        </div>
      )}
    </div>
  );
}
